import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

export default function StaffDescuentos() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/admin/discount-codes").then((r) => {
    const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.data || []);
    setItems(list);
  }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("¿Eliminar código?")) return;
    try {
      await api.delete(`/admin/discount-codes/${id}`);
      toast.success("Eliminado"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (id, isActive) => {
    try {
      await api.put(`/admin/discount-codes/${id}`, { is_active: !isActive });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Descuentos</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Códigos de descuento</h1>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-discount-btn"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] text-sm font-semibold">
            <Plus className="w-4 h-4" /> Nuevo código
          </button>
        </div>

        <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="discounts-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Código</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-right px-3 py-2">Valor</th>
                <th className="text-right px-3 py-2">Usos</th>
                <th className="text-left px-3 py-2">Válido hasta</th>
                <th className="text-left px-3 py-2">Activo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-gray-500">Sin códigos.</td></tr>
              )}
              {items.map((d) => {
                const isPercent = d.percent_off != null;
                const rawValue = isPercent ? d.percent_off : d.amount_off_mxn;
                const displayValue = rawValue == null
                  ? "—"
                  : isPercent
                    ? `${Number(rawValue)}%`
                    : `$${Number(rawValue).toLocaleString("es-MX")}`;
                return (
                  <tr key={d.id} className="border-t border-gray-800" data-testid={`discount-row-${d.code}`}>
                    <td className="px-3 py-2 font-mono text-white">{d.code}</td>
                    <td className="px-3 py-2 text-xs uppercase text-gray-300">
                      {isPercent ? "percent" : "fixed"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-white">{displayValue}</td>
                    <td className="px-3 py-2 text-right font-mono text-white">
                      {d.used_count || 0}{d.max_uses ? `/${d.max_uses}` : ""}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-300">{d.valid_until?.slice(0, 10) || "—"}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => toggle(d.id, d.is_active)}
                        data-testid={`discount-toggle-${d.code}`}
                        className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${
                          d.is_active ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-gray-500/10 text-gray-400 border-gray-700"
                        }`}>
                        {d.is_active ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => remove(d.id)} data-testid={`discount-delete-${d.code}`}
                        aria-label="Eliminar" className="text-red-400 hover:text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewModal onClose={() => { setShowNew(false); load(); }} />}
    </StaffLayout>
  );
}

function NewModal({ onClose }) {
  const [form, setForm] = useState({
    code: "", type: "percent", value: 10, max_uses: null,
    applies_to_program_ids: [], valid_from: null, valid_until: null, is_active: true,
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const value = parseFloat(form.value);
      const payload = {
        code: form.code.toUpperCase(),
        percent_off: form.type === "percent" ? value : null,
        amount_off_mxn: form.type === "fixed" ? value : null,
        max_uses: form.max_uses,
        applies_to_program_ids: form.applies_to_program_ids,
        valid_from: form.valid_from,
        valid_until: form.valid_until,
        is_active: form.is_active,
      };
      await api.post("/admin/discount-codes", payload);
      toast.success("Código creado");
      onClose();
    } catch (er) { toast.error(formatApiError(er)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-discount-modal"
        className="w-full max-w-md bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3">
        <div className="flex justify-between">
          <h3 className="text-xl font-semibold text-white">Nuevo código</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-gray-400"><X /></button>
        </div>
        <F label="Código *" val={form.code} on={(v) => setForm({ ...form, code: v })} testid="nd-code" required />
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Tipo</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            data-testid="nd-type"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
            <option value="percent">Porcentaje</option>
            <option value="fixed">Fijo (MXN)</option>
          </select>
        </div>
        <F label="Valor *" type="number" val={form.value} on={(v) => setForm({ ...form, value: parseFloat(v) })} testid="nd-value" required />
        <F label="Máx usos (vacío=infinito)" type="number" val={form.max_uses || ""} on={(v) => setForm({ ...form, max_uses: v ? parseInt(v, 10) : null })} testid="nd-max" />
        <F label="Válido hasta" type="date" val={form.valid_until || ""} on={(v) => setForm({ ...form, valid_until: v || null })} testid="nd-valid-until" />
        <button type="submit" data-testid="nd-submit"
          className="w-full py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold">Crear</button>
      </form>
    </div>
  );
}

function F({ label, val, on, testid, required, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-gray-400">{label}</span>
      <input value={val} onChange={(e) => on(e.target.value)} required={required} type={type}
        data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
    </label>
  );
}
